import User from '@js/common/user'

import {
  DEFAULT_EQUITY_VALUE,
  LIST_OF_ALLOWED_PARAMS,
  QUIZ_CONFIG_CONVERSION_TYPES
} from '@f/applications/QuizApp/partials/quizConstants'
import {
  AutofillValidationState,
  ConversionType,
  DefinedValueSet,
  EquityRange,
  NoValueSet,
  ProfileRequestPayload,
  ProspectTrack,
  QuizStep,
  StepValue
} from '@f/applications/QuizApp/partials/types'

export const budgetRanges = [
  { min: 1, max: 100000, range: '< 100.000' },
  { min: 100000, max: 150000, range: '100.000 - 150.000' },
  { min: 150000, max: 200000, range: '150.000 - 200.000' },
  { min: 200000, max: 250000, range: '200.000 - 250.000' },
  { min: 250000, max: 300000, range: '250.000 - 300.000' },
  { min: 300000, max: 350000, range: '300.000 - 350.000' },
  { min: 350000, max: 400000, range: '350.000 - 400.000' },
  { min: 400000, max: 450000, range: '400.000 - 450.000' },
  { min: 450000, max: 500000, range: '450.000 - 500.000' },
  { min: 500000, max: 550000, range: '500.000 - 550.000' },
  { min: 550000, max: 600000, range: '550.000 - 600.000' },
  { min: 600000, max: Infinity, range: '> 600.000' }
]

export const budgetRangeMappedValue = (value: number | 'na' = 0) => {
  if (value === 'na') {
    return 'na'
  }

  const DEFAULT_BUDGET = budgetRanges[4]

  const budget =
    budgetRanges.find(b => b.min <= value && value < b.max) || DEFAULT_BUDGET
  return budget.range
}

const equityRanges: EquityRange[] = [
  { min: 0, max: 25_000, range: '0 - 25.000' },
  { min: 25_000, max: 50_000, range: '25.000 - 50.000' },
  { min: 50_000, max: 75_000, range: '50.000 - 75.000' },
  { min: 75_000, max: 100_000, range: '75.000 - 100.000' },
  { min: 100_000, max: 150_000, range: '100.000 - 150.000' },
  { min: 150_000, max: 200_000, range: '150.000 - 200.000' },
  { min: 200_000, max: 250_000, range: '200.000 - 250.000' },
  { min: 250_000, max: 300_000, range: '250.000 - 300.000' },
  { min: 300_000, max: 350_000, range: '300.000 - 350.000' },
  { min: 350_000, max: 400_000, range: '350.000 - 400.000' },
  { min: 400_000, max: 450_000, range: '400.000 - 450.000' },
  { min: 450_000, max: 500_000, range: '450.000 - 500.000' },
  { min: 500_000, max: 550_000, range: '500.000 - 550.000' },
  { min: 550_000, max: 600_000, range: '550.000 - 600.000' },
  { min: 600_000, max: Infinity, range: '> 600.000' }
]

export const getEquityRange = (
  equity: string | number = DEFAULT_EQUITY_VALUE
) => {
  const equityValue = typeof equity === 'string' ? parseInt(equity) : equity
  const equityRange = equityRanges.find(
    range => equityValue >= range.min && equityValue < range.max
  ) as EquityRange

  return equityRange.range
}

const livingAreas = [
  { value: 75, rounded: '75', range: '< 75' },
  { value: 100, rounded: '100', range: '75 - 100' },
  { value: 120, rounded: '120', range: '100 - 120' },
  { value: 140, rounded: '140', range: '120 - 140' },
  { value: 160, rounded: '160', range: '140 - 160' },
  { value: 180, rounded: '180', range: '160 - 180' },
  { value: 200, rounded: '200', range: '180 - 200' },
  { value: 225, rounded: '225', range: '200 - 225' },
  { value: 250, rounded: '250', range: '225 - 250' },
  { value: 275, rounded: '275', range: '250 - 275' },
  { value: 300, rounded: '300', range: '275 - 300' }
]

export const mapLivingAreaTotalValueToRange = value => {
  if (!value) {
    return 'na'
  }

  const match = livingAreas.find(area => value < area.value)
  return match ? match.range : '> 300'
}

export const computeProfileUpdatePayload = ({
  stepValues,
  conversionType,
  publicId,
  exclusiveVendorNid,
  autofillValidation,
  conversionValue = {}
}: {
  stepValues: StepValue[]
  conversionType: ConversionType
  publicId: string
  exclusiveVendorNid: string
  autofillValidation: AutofillValidationState
  conversionValue?: {
    prospect_track: ProspectTrack
    contract_probability_base: number
  }
}) => {
  const filterAllowedParams = ({ name }) =>
    LIST_OF_ALLOWED_PARAMS.includes(name)

  const payload: ProfileRequestPayload = stepValues
    .filter(filterAllowedParams)
    .reduce((acc, { name, value }) => {
      let finalName = name
      let finalValue = value
      if (name === 'bauort') {
        finalName = 'delivery_region'
        finalValue = `{ "postcode": "${finalValue}" }`
      } else if (name === 'budget_range') {
        finalValue = budgetRangeMappedValue(finalValue)
      } else if (name === 'house_type' || name === 'two_families_house_type') {
        finalName = 'utilization'
      } else if (name === 'living_area_total') {
        finalValue = mapLivingAreaTotalValueToRange(value)
      } else if (name === 'property_type') {
        finalValue = value === 'na' ? null : value
      }

      return { ...acc, [finalName]: finalValue }
    }, {})

  const location = User.getLocation()
  if (location.city && location.state) {
    payload.delivery_region_city_name = location.city
    payload.delivery_region_state = location.state.long_name
    payload.ua_delivery_region_verbose = location
  }
  if (conversionType === QUIZ_CONFIG_CONVERSION_TYPES.financeStandalone) {
    payload.is_standalone_finance_user = true
  }

  if (publicId) {
    payload.public_id = publicId
  }

  if (exclusiveVendorNid) {
    payload.entity = 'EX'
  }

  if (conversionValue) {
    payload.contract_probability_base =
      conversionValue.contract_probability_base
    payload.prospect_track = conversionValue.prospect_track
  }

  if (autofillValidation) {
    Object.entries(autofillValidation).forEach(([key, value]) => {
      payload[`autofill_validation_${key}`] = value
    })
  }

  return payload
}

export const getValidationTrackingDisplayLabel = validationState => {
  if (!validationState.has_valid_email && !validationState.has_valid_phone) {
    return 'email-phone'
  }
  if (!validationState.has_valid_email && validationState.has_valid_phone) {
    return 'email'
  }
  if (validationState.has_valid_email && !validationState.has_valid_phone) {
    return 'phone'
  }
  return ''
}

export function composeStreetName(streetName: string, streetNumber: number) {
  const trimmedStreetName = streetName.trim()
  if (streetNumber == null) {
    return trimmedStreetName
  }

  const streetNumberStr = String(streetNumber).trim()
  if (trimmedStreetName.endsWith(streetNumberStr)) {
    return trimmedStreetName
  }

  return `${trimmedStreetName} ${streetNumberStr}`
}

export function calculateFinancialDetailsSliderDefaults(step, budget) {
  function roundToNextIteration(number, iterationSize) {
    return Math.ceil(number / iterationSize) * iterationSize
  }
  const DEFAULT_LAND_VALUE = 112500
  const AVERAGE_LOAN_CONTRIBUTION_RATIO = 0.8
  const AVERAGE_MONTHLY_INCOMES_IN_LOAN = 90

  const maxExpectedValue = step.sliders.find(
    slide => slide.name === 'expected_value'
  ).maxValue

  const expectedValueIterationSize = step.sliders.find(
    slide => slide.name === 'expected_value'
  ).valueStep
  const loanAmountIterationSize = step.sliders.find(
    slide => slide.name === 'loan_amount'
  ).valueStep
  const monthlyIncomeIterationSize = step.sliders.find(
    slide => slide.name === 'monthly_income'
  ).valueStep

  const defaultPropertyValueIncludingLand = roundToNextIteration(
    Number(budget) + DEFAULT_LAND_VALUE > maxExpectedValue
      ? maxExpectedValue
      : Number(budget) + DEFAULT_LAND_VALUE,
    expectedValueIterationSize
  )

  const defaultLoanAmount = roundToNextIteration(
    defaultPropertyValueIncludingLand * AVERAGE_LOAN_CONTRIBUTION_RATIO,
    loanAmountIterationSize
  )

  const expectedValue = {
    ...step.values.find((value: StepValue) => value.name === 'expected_value'),
    value: defaultPropertyValueIncludingLand
  }
  const loanAmount = {
    ...step.values.find((value: StepValue) => value.name === 'loan_amount'),
    value: defaultLoanAmount
  }
  const equity = {
    ...step.values.find((value: StepValue) => value.name === 'equity'),
    value: defaultPropertyValueIncludingLand - defaultLoanAmount
  }
  const monthlyIncome = {
    ...step.values.find((value: StepValue) => value.name === 'monthly_income'),
    value: roundToNextIteration(
      (defaultPropertyValueIncludingLand * AVERAGE_LOAN_CONTRIBUTION_RATIO) /
        AVERAGE_MONTHLY_INCOMES_IN_LOAN,
      monthlyIncomeIterationSize
    )
  }

  return [expectedValue, loanAmount, equity, monthlyIncome]
}

function hasValue(
  value: NoValueSet | DefinedValueSet
): value is DefinedValueSet {
  return Object.prototype.hasOwnProperty.call(value, 'value')
}

function isNested(value: DefinedValueSet['value']): value is StepValue[] {
  return (
    Array.isArray(value) &&
    typeof value[0] !== 'string' &&
    Boolean(value[0]?.name)
  )
}

const formatName = (name: string) => name.replace('-', '_')

const flattenNestedValues = (stepValue: StepValue[]) => [
  ...stepValue.map(v => ({
    name: formatName(v.name),
    value: v.value
  }))
]

export const accumulatePayloadsFromAllSteps = (steps: QuizStep[]) =>
  steps.reduce<StepValue[]>((acc, currentStep) => {
    for (const value of currentStep.values) {
      if (hasValue(value)) {
        const stepValue = value.value
        if (isNested(stepValue)) {
          const flattenedValues = flattenNestedValues(stepValue)
          acc.push(...flattenedValues)
        } else {
          acc.push({ name: value.name, value: stepValue })
        }
      }
    }

    return acc
  }, [])

export function isHouseTypeQuiz(conversionType: ConversionType) {
  return conversionType === QUIZ_CONFIG_CONVERSION_TYPES.house
}

export function isFinanceTypeQuiz(conversionType: ConversionType) {
  return (
    conversionType === QUIZ_CONFIG_CONVERSION_TYPES.finance ||
    conversionType === QUIZ_CONFIG_CONVERSION_TYPES.financeStandalone
  )
}

export function isSellerTypeQuiz(conversionType: ConversionType) {
  return conversionType === QUIZ_CONFIG_CONVERSION_TYPES.sellerConversion
}
